/**
 * The SEO document's pure transitions and resolutions: what the page edits, what the hook autosaves, and what the
 * inheritance tags show. No React, no request.
 */
import { isFooterHtmlEmpty } from './footer-html';
import { orderFooterRules, matchFooterRule } from './footer-rules';
import { footerProbePath, footerRulePathOf, matchOfPath } from './route-path';
import type {
  ImageRef,
  Localized,
  SiteSeoAi,
  SiteSeoDocument,
  SiteSeoFooterRule,
  SiteSeoGenerated,
  SiteSeoGenerateField,
  SiteSeoKeywords,
  SiteSeoLiveMeta,
  SiteSeoPage,
  SiteSeoRoute,
  SiteSeoSite,
} from './seo-types';
import { KEYWORD_MAX, KEYWORDS_MAX } from './seo-types';

export type SeoTextField = 'title' | 'description' | 'footer_text';
export type SeoSiteTextField = 'title_template' | 'description' | 'footer_text';
export type ValueSource = 'own' | 'default-language' | 'site' | 'wildcard' | 'platform' | 'none';

export interface ResolvedValue {
  value: string;
  source: ValueSource;
  fromPath?: string;
}

export const emptySiteSeoDocument = (): SiteSeoDocument => ({
  site: { ai: { allow_search_crawlers: true, allow_training_crawlers: true } },
  routes: [],
});

/* ── normalisation (the wire rule: '' and empty maps/lists dropped, routes by path, keys sorted) ── */

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normaliseValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normaliseValue).filter((v) => v !== undefined && v !== '');
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value).sort()) {
    const next = normaliseValue(value[key]);
    if (next === undefined || next === '') continue;
    if (isPlainObject(next) && Object.keys(next).length === 0) continue;
    if (Array.isArray(next) && next.length === 0) continue;
    out[key] = next;
  }
  return out;
};

/* ── keywords (MIRRORS spark-creatives `seo.compactKeywords`: change both or neither) ── */

/** An HTML tag inside a keyword (Go `keywordTag`). */
const KEYWORD_TAG_RE = /<[^<>]*>/g;
/** Go `strings.Fields`' whitespace (`unicode.IsSpace`): ASCII spaces, NEL, NBSP and the Unicode space separators. */
const GO_SPACE_RE = /[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/;

/**
 * One language's keyword list, normalised as the server stores it, in the server's order: per keyword, whole HTML
 * tags removed, then any stray `<` / `>`, Unicode whitespace runs collapsed to one space and trimmed, '' dropped;
 * duplicates dropped case-insensitively keeping the first; the first {@link KEYWORDS_MAX} kept; each then cut to
 * {@link KEYWORD_MAX} code points and trimmed.
 */
export const normaliseKeywords = (list: readonly string[]): string[] => {
  const kept: string[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    const keyword = raw
      .replace(KEYWORD_TAG_RE, '')
      .replace(/[<>]/g, '')
      .split(GO_SPACE_RE)
      .filter((word) => word !== '')
      .join(' ');
    const key = keyword.toLowerCase();
    if (keyword === '' || seen.has(key)) continue;
    seen.add(key);
    kept.push(keyword);
    if (kept.length === KEYWORDS_MAX) break;
  }
  return kept.map((keyword) => {
    const runes = Array.from(keyword);
    return runes.length > KEYWORD_MAX ? runes.slice(0, KEYWORD_MAX).join('').trim() : keyword;
  });
};

/** Every language normalised; a language left with none dropped; undefined when nothing is left (Go nil). */
export const normaliseKeywordsMap = (map: SiteSeoKeywords | undefined): SiteSeoKeywords | undefined => {
  if (!map) return undefined;
  const out: SiteSeoKeywords = {};
  for (const [language, list] of Object.entries(map)) {
    const kept = normaliseKeywords(list);
    if (kept.length) out[language] = kept;
  }
  return Object.keys(out).length ? out : undefined;
};

/** The map with `language` set to `list` normalised; a list left empty deletes the language; undefined when empty. */
const withKeywordsLanguage = (
  map: SiteSeoKeywords | undefined,
  language: string,
  list: readonly string[],
): SiteSeoKeywords | undefined => normaliseKeywordsMap({ ...(map ?? {}), [language]: [...list] });

const byPath = (a: SiteSeoRoute, b: SiteSeoRoute): number => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0);

export const normaliseDocument = (doc: SiteSeoDocument): SiteSeoDocument => {
  const site = normaliseValue(doc.site) as SiteSeoSite;
  const routes = [...doc.routes].sort(byPath).map((r) => normaliseValue(r) as SiteSeoRoute);
  return { routes, site: { ...site, ai: site.ai ?? { allow_search_crawlers: true, allow_training_crawlers: true } } };
};

const keyOf = (value: unknown): string => JSON.stringify(normaliseValue(value));

export const sameDocument = (a: SiteSeoDocument | null, b: SiteSeoDocument | null): boolean => {
  if (a === null || b === null) return a === b;
  return JSON.stringify(normaliseDocument(a)) === JSON.stringify(normaliseDocument(b));
};

/* ── routes ─────────────────────────────────────────────────────────────────── */

export const routeOf = (doc: SiteSeoDocument, path: string): SiteSeoRoute | undefined =>
  doc.routes.find((r) => r.path === path);

/** Adds a `site_page` row for every page path the document lacks; never removes; keeps everything else. Idempotent. */
export const documentFromPages = (base: SiteSeoDocument | null, pages: readonly SiteSeoPage[]): SiteSeoDocument => {
  const doc = base ?? emptySiteSeoDocument();
  const known = new Set(doc.routes.map((r) => r.path));
  const added: SiteSeoRoute[] = [];
  for (const page of pages) {
    if (known.has(page.path)) continue;
    known.add(page.path);
    added.push({ path: page.path, match: page.match, origin: 'site_page' });
  }
  return added.length ? { ...doc, routes: [...doc.routes, ...added] } : doc;
};

/** `site_page` routes whose path is no longer among the pages (kept in the draft; the server prunes at publish). */
export const orphanSitePages = (doc: SiteSeoDocument, pages: readonly SiteSeoPage[]): string[] => {
  const live = new Set(pages.map((p) => p.path));
  return doc.routes.filter((r) => r.origin === 'site_page' && !live.has(r.path)).map((r) => r.path);
};

export const addCustomRoute = (doc: SiteSeoDocument, path: string): SiteSeoDocument =>
  routeOf(doc, path) ? doc : { ...doc, routes: [...doc.routes, { path, match: matchOfPath(path), origin: 'custom' }] };

/** Removes a custom route; a `site_page` path is returned unchanged. */
export const removeRoute = (doc: SiteSeoDocument, path: string): SiteSeoDocument => {
  const route = routeOf(doc, path);
  if (!route || route.origin !== 'custom') return doc;
  return { ...doc, routes: doc.routes.filter((r) => r.path !== path) };
};

const updateRoute = (
  doc: SiteSeoDocument,
  path: string,
  accepts: (route: SiteSeoRoute) => boolean,
  update: (route: SiteSeoRoute) => SiteSeoRoute,
): SiteSeoDocument => {
  const route = routeOf(doc, path);
  if (!route || !accepts(route)) return doc;
  return { ...doc, routes: doc.routes.map((r) => (r.path === path ? update(r) : r)) };
};

/** The map with `language` set to `value`; '' deletes the key; undefined when the map ends empty. */
const withLanguage = (
  map: Localized<string> | undefined,
  language: string,
  value: string,
): Localized<string> | undefined => {
  const next: Localized<string> = { ...(map ?? {}) };
  if (value === '') delete next[language];
  else next[language] = value;
  return Object.keys(next).length ? next : undefined;
};

/** A copy of `obj` with `key` set, or removed when `value` is undefined. */
const withField = <T extends object, K extends keyof T>(obj: T, key: K, value: T[K] | undefined): T => {
  const next = { ...obj };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
};

const notWildcard = (route: SiteSeoRoute): boolean => route.match !== 'wildcard';

/** '' deletes the language key; an empty map deletes the field; title/description on a wildcard are ignored. */
export const setRouteText = (
  doc: SiteSeoDocument,
  path: string,
  field: SeoTextField,
  language: string,
  value: string,
): SiteSeoDocument =>
  updateRoute(
    doc,
    path,
    (route) => field === 'footer_text' || notWildcard(route),
    (route) => withField(route, field, withLanguage(route[field], language, value)),
  );

export const setRouteImage = (doc: SiteSeoDocument, path: string, image: ImageRef | undefined): SiteSeoDocument =>
  updateRoute(doc, path, notWildcard, (route) => withField(route, 'share_image', image));

/** `false` drops the flag (absent = indexable), so toggling back leaves no trace in the document. */
export const setRouteFlag = (doc: SiteSeoDocument, path: string, field: 'noindex', value: boolean): SiteSeoDocument =>
  updateRoute(doc, path, notWildcard, (route) => withField(route, field, value ? true : undefined));

/** Exact routes only; '' deletes. */
export const setRouteCanonical = (doc: SiteSeoDocument, path: string, value: string): SiteSeoDocument =>
  updateRoute(
    doc,
    path,
    (route) => route.match === 'exact',
    (route) => withField(route, 'canonical', value === '' ? undefined : value),
  );

/** Not on wildcards (they carry no page to score); see {@link normaliseKeywords} for what is stored. */
export const setRouteKeywords = (
  doc: SiteSeoDocument,
  path: string,
  language: string,
  list: readonly string[],
): SiteSeoDocument =>
  updateRoute(doc, path, notWildcard, (route) =>
    withField(route, 'keywords', withKeywordsLanguage(route.keywords, language, list)),
  );

/* ── site ───────────────────────────────────────────────────────────────────── */

/** The site's keywords: context for every page (and Lumio), never a page's focus. */
export const setSiteKeywords = (doc: SiteSeoDocument, language: string, list: readonly string[]): SiteSeoDocument => ({
  ...doc,
  site: withField(doc.site, 'keywords', withKeywordsLanguage(doc.site.keywords, language, list)),
});

export const setSiteText = (
  doc: SiteSeoDocument,
  field: SeoSiteTextField,
  language: string,
  value: string,
): SiteSeoDocument => ({
  ...doc,
  site: withField(doc.site, field, withLanguage(doc.site[field], language, value)),
});

export const setSiteImage = (doc: SiteSeoDocument, image: ImageRef | undefined): SiteSeoDocument => ({
  ...doc,
  site: withField(doc.site, 'share_image', image),
});

/** Patches the AI section; an `llms_*` set to '' or undefined is removed. */
export const setSiteAi = (doc: SiteSeoDocument, patch: Partial<SiteSeoAi>): SiteSeoDocument => {
  let ai: SiteSeoAi = { ...doc.site.ai, ...patch };
  if (!ai.llms_summary) ai = withField(ai, 'llms_summary', undefined);
  if (!ai.llms_facts) ai = withField(ai, 'llms_facts', undefined);
  return { ...doc, site: { ...doc.site, ai } };
};

export const resetSiteField = (doc: SiteSeoDocument, field: SeoSiteTextField | 'share_image'): SiteSeoDocument => ({
  ...doc,
  site: withField(doc.site, field, undefined),
});

/** "Reset to platform default" for a site text in `language`: on the default language the field goes (the platform
 *  default in every language); on another one only that language goes, so the page falls back to the default
 *  language's text, never losing it. */
export const resetSiteText = (
  doc: SiteSeoDocument,
  field: SeoSiteTextField,
  language: string,
  defaultLanguage: string,
): SiteSeoDocument =>
  language === defaultLanguage ? resetSiteField(doc, field) : setSiteText(doc, field, language, '');

/* ── resolution ─────────────────────────────────────────────────────────────── */

const textIn = (map: Localized<string> | undefined, language: string): string => map?.[language] ?? '';

/**
 * The text the site serves for a route field, as the server publishes it (MIRRORS spark-creatives `pkg/seo`
 * `baseMeta` / `translatedMeta`): own; in a language other than the default, the site's description in that language
 * (`translatedMeta`), and only without either the default-language meta (`baseMeta`): the route's own, then the site's
 * description; then the platform; then none. `liveMeta` null = not loaded = none.
 */
export const resolveRouteText = (
  doc: SiteSeoDocument,
  liveMeta: Localized<SiteSeoLiveMeta> | null,
  path: string,
  field: 'title' | 'description',
  language: string,
  defaultLanguage: string,
): ResolvedValue => {
  const route = routeOf(doc, path);
  const own = textIn(route?.[field], language);
  if (own) return { value: own, source: 'own' };
  if (field === 'description' && language !== defaultLanguage) {
    const site = textIn(doc.site.description, language);
    if (site) return { value: site, source: 'site' };
  }
  const fallback = textIn(route?.[field], defaultLanguage);
  if (fallback) return { value: fallback, source: 'default-language' };
  if (field === 'description') {
    const site = textIn(doc.site.description, defaultLanguage);
    if (site) return { value: site, source: 'site' };
  }
  const platform = (liveMeta?.[language] ?? liveMeta?.[defaultLanguage])?.[field] ?? '';
  if (platform) return { value: platform, source: 'platform' };
  return { value: '', source: 'none' };
};

/**
 * The title template a resolved title is shown through, as the server applies it: a title in `language` through that
 * language's template, else the default one; a default-language title (the base meta) through the default one only.
 */
export const titleTemplateFor = (
  doc: SiteSeoDocument,
  source: ValueSource,
  language: string,
  defaultLanguage: string,
): string | undefined => {
  const templates = doc.site.title_template;
  if (source === 'default-language') return textIn(templates, defaultLanguage) || undefined;
  return textIn(templates, language) || textIn(templates, defaultLanguage) || undefined;
};

/** Where a route's keywords come from. A route never takes the site's keywords (they are never a page's focus). */
export type KeywordsSource = 'own' | 'default-language' | 'none';

/**
 * The keywords a route uses in `language` (path null = the site level): its own in `language`, else its own in
 * `defaultLanguage`, else none. `language` is the one they came from ('' when none). Mirrors the server's
 * `seo.EffectiveKeywords`.
 */
export const resolveRouteKeywords = (
  doc: SiteSeoDocument,
  path: string | null,
  language: string,
  defaultLanguage: string,
): { keywords: string[]; source: KeywordsSource; language: string } => {
  const own = path === null ? doc.site.keywords : routeOf(doc, path)?.keywords;
  const inLanguage = own?.[language] ?? [];
  if (inLanguage.length) return { keywords: inLanguage, source: 'own', language };
  const inDefault = own?.[defaultLanguage] ?? [];
  if (inDefault.length) return { keywords: inDefault, source: 'default-language', language: defaultLanguage };
  return { keywords: [], source: 'none', language: '' };
};

const hasText = (map: Localized<string> | undefined): map is Localized<string> =>
  map !== undefined && Object.values(map).some((v) => v !== '');

/** The owner's footer rules: `site.footer_text` as `*`, then every route's footer under its rule path, in document order. */
export const ownerFooterRules = (doc: SiteSeoDocument): SiteSeoFooterRule[] => {
  const rules: SiteSeoFooterRule[] = [];
  if (hasText(doc.site.footer_text)) rules.push({ path: '*', text: doc.site.footer_text, origin: 'owner' });
  for (const route of doc.routes) {
    const rulePath = footerRulePathOf(route);
    if (rulePath === null || !hasText(route.footer_text)) continue;
    rules.push({ path: rulePath, text: route.footer_text, origin: 'owner' });
  }
  return rules;
};

/** Every footer rule the site matches over, in its order: the owner rules plus the system rules none overrides. */
export const footerRulesFor = (
  doc: SiteSeoDocument,
  liveFooterRules: readonly SiteSeoFooterRule[],
): SiteSeoFooterRule[] => {
  const owner = ownerFooterRules(doc);
  const ownerPaths = new Set(owner.map((r) => r.path));
  const system = liveFooterRules
    .filter((r) => !ownerPaths.has(r.path))
    .map((r): SiteSeoFooterRule => ({ ...r, origin: 'system' }));
  return orderFooterRules([...owner, ...system]);
};

/** {@link resolveFooterText} over rules already built by {@link footerRulesFor} (one build for many routes). */
export const resolveFooterTextIn = (
  ordered: readonly SiteSeoFooterRule[],
  route: SiteSeoRoute | null,
  language: string,
  defaultLanguage: string,
): ResolvedValue => {
  const probe = route ? footerProbePath(route) : null;
  // Site defaults (and a route with no probe) show the `*` rule: probing `/` would let a `/` rule win over it.
  const rule = probe === null ? ordered.find((r) => r.path === '*') : matchFooterRule(ordered, probe);
  if (!rule) return { value: '', source: 'none' };
  const value = textIn(rule.text, language) || textIn(rule.text, defaultLanguage);
  const ownPath = route ? footerRulePathOf(route) : null;
  const source: ValueSource =
    rule.origin === 'system' ? 'platform' : rule.path === '*' ? 'site' : rule.path === ownPath ? 'own' : 'wildcard';
  return { value, source, fromPath: rule.path };
};

/**
 * The footer text the site shows for `route` (null = Site defaults, which show the `*` rule), through the site's own
 * matcher over the owner rules plus the system rules no owner rule overrides. A route with no probe (a pattern deriving
 * no wildcard) resolves like Site defaults. The matched rule's text in `language`, else `defaultLanguage` (the site does
 * not fall through to another rule when the text is empty, so neither does this).
 */
export const resolveFooterText = (
  doc: SiteSeoDocument,
  liveFooterRules: readonly SiteSeoFooterRule[],
  route: SiteSeoRoute | null,
  language: string,
  defaultLanguage: string,
): ResolvedValue => resolveFooterTextIn(footerRulesFor(doc, liveFooterRules), route, language, defaultLanguage);

/* ── summaries ──────────────────────────────────────────────────────────────── */

const ROUTE_TEXT_FIELDS: readonly SeoTextField[] = ['title', 'description', 'footer_text'];

/** Paths with any text field set in the default language but not in `language`. */
export const routesMissingLanguage = (doc: SiteSeoDocument, language: string, defaultLanguage: string): string[] =>
  doc.routes
    .filter((route) =>
      ROUTE_TEXT_FIELDS.some((f) => textIn(route[f], defaultLanguage) !== '' && textIn(route[f], language) === ''),
    )
    .map((r) => r.path);

/**
 * Paths whose normalised route differs between draft and published (either side), plus `*` when the site differs.
 * Keywords are ignored everywhere (never published, so never an unpublished change), as the server's `Same` does.
 */
export const changedRoutes = (draft: SiteSeoDocument, published: SiteSeoDocument | null): Set<string> => {
  const changed = new Set<string>();
  // A site_page row with nothing written is how the document lists the site's pages, so it stands for "no route":
  // its presence on one side only (nothing published yet, a page added to or dropped from the site) is not a change.
  // noindex: false is the default the server always sends (x-omitempty false), so it compares as absent.
  const keyOrAbsent = (r: SiteSeoRoute): string | undefined => {
    const key = keyOf({ ...r, noindex: r.noindex === true ? true : undefined, keywords: undefined });
    return r.origin === 'site_page' && key === keyOf({ path: r.path, match: r.match, origin: r.origin })
      ? undefined
      : key;
  };
  const before = new Map((published?.routes ?? []).map((r) => [r.path, keyOrAbsent(r)]));
  const after = new Map(draft.routes.map((r) => [r.path, keyOrAbsent(r)]));
  for (const [path, key] of after) if (before.get(path) !== key) changed.add(path);
  for (const [path, key] of before) if (!after.has(path) && key !== undefined) changed.add(path);
  const siteKey = (site: SiteSeoSite): string =>
    keyOf(normaliseDocument({ site: { ...site, keywords: undefined }, routes: [] }).site);
  if (siteKey(draft.site) !== siteKey((published ?? emptySiteSeoDocument()).site)) changed.add('*');
  return changed;
};

/**
 * Text meta as the server writes it: every `'` becomes `’`, and `"` alternates `“` / `”` starting with the opening one.
 * MIRRORS spark-creatives `pkg/seo` `typographic`: change both or neither.
 */
export const typographic = (value: string): string => {
  if (!/['"]/.test(value)) return value;
  let open = true;
  let out = '';
  for (const ch of value) {
    if (ch === "'") out += '’';
    else if (ch === '"') {
      out += open ? '“' : '”';
      open = !open;
    } else out += ch;
  }
  return out;
};

/**
 * True when the live default-language title/description differ from what the owner published for this exact or
 * pattern route (the title through the site template), compared as the server writes them (see {@link typographic}).
 * False when nothing is published for it or `liveMeta` is null.
 */
export const platformChanged = (
  published: SiteSeoDocument | null,
  path: string,
  liveMeta: Localized<SiteSeoLiveMeta> | null,
  defaultLanguage: string,
  template: (page: string) => string,
): boolean => {
  if (!published || !liveMeta) return false;
  const route = routeOf(published, path);
  if (!route || route.match === 'wildcard') return false;
  const live = liveMeta[defaultLanguage];
  const title = textIn(route.title, defaultLanguage);
  const description = textIn(route.description, defaultLanguage);
  if (title && typographic(template(title)) !== (live?.title ?? '')) return true;
  if (description && typographic(description) !== (live?.description ?? '')) return true;
  return false;
};

/** Exact + pattern routes, and those whose default-language description is absent on the route and on the site. */
export const countWithoutDescription = (
  doc: SiteSeoDocument,
  defaultLanguage: string,
): { pages: number; withoutDescription: number } => {
  const pages = doc.routes.filter((r) => r.match !== 'wildcard');
  const siteDescribed = textIn(doc.site.description, defaultLanguage) !== '';
  const withoutDescription = siteDescribed
    ? 0
    : pages.filter((r) => textIn(r.description, defaultLanguage) === '').length;
  return { pages: pages.length, withoutDescription };
};

/* ── generation ─────────────────────────────────────────────────────────────── */

const SITE_TEXT_FIELDS: readonly SeoSiteTextField[] = ['title_template', 'description', 'footer_text'];
const isSiteTextField = (field: string): field is SeoSiteTextField =>
  (SITE_TEXT_FIELDS as readonly string[]).includes(field);
const isRouteTextField = (field: string): field is SeoTextField =>
  (ROUTE_TEXT_FIELDS as readonly string[]).includes(field);

/**
 * Merges a Lumio answer: only `req.fields` present (non-empty) in `generated`; never clears. Site level when
 * `req.path` is absent (`llms_*` into `site.ai`, the site text fields into `site`); route text fields otherwise.
 */
export const mergeGenerated = (
  doc: SiteSeoDocument,
  req: { path?: string; language: string; fields: readonly string[] },
  generated: Partial<Record<string, string>>,
): SiteSeoDocument => {
  let next = doc;
  for (const field of req.fields) {
    const value = generated[field];
    if (!value) continue;
    if (req.path === undefined) {
      if (field === 'llms_summary' || field === 'llms_facts') next = setSiteAi(next, { [field]: value });
      else if (isSiteTextField(field)) next = setSiteText(next, field, req.language, value);
    } else if (isRouteTextField(field)) {
      next = setRouteText(next, req.path, field, req.language, value);
    }
  }
  return next;
};

/** The field's current value where `req` would write it ('' when absent). */
const currentGeneratedValue = (
  doc: SiteSeoDocument,
  req: { path?: string; language: string },
  field: SiteSeoGenerateField,
): string => {
  if (req.path === undefined) {
    if (field === 'llms_summary' || field === 'llms_facts') return doc.site.ai[field] ?? '';
    return isSiteTextField(field) ? textIn(doc.site[field], req.language) : '';
  }
  const route = routeOf(doc, req.path);
  return route && isRouteTextField(field) ? textIn(route[field], req.language) : '';
};

const isEmptyValue = (field: SiteSeoGenerateField, value: string): boolean =>
  field === 'footer_text' ? isFooterHtmlEmpty(value) : value.trim() === '';

/**
 * A Lumio answer that fills only: each asked field lands only where its current value is empty (a footer with no
 * text, e.g. `<p></p>`, is empty). `landed` lists the fields actually written, in `req.fields` order; nothing landed
 * returns `doc` itself.
 */
export const fillGenerated = (
  doc: SiteSeoDocument,
  req: { path?: string; language: string; fields: readonly SiteSeoGenerateField[] },
  generated: SiteSeoGenerated,
): { doc: SiteSeoDocument; landed: SiteSeoGenerateField[] } => {
  let next = doc;
  const landed: SiteSeoGenerateField[] = [];
  for (const field of req.fields) {
    if (!generated[field] || !isEmptyValue(field, currentGeneratedValue(next, req, field))) continue;
    const written = mergeGenerated(next, { path: req.path, language: req.language, fields: [field] }, generated);
    if (written === next) continue;
    next = written;
    landed.push(field);
  }
  return { doc: next, landed };
};
